import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, History, ChevronRight } from "lucide-react";
import { api, MARKET_ORDER, MARKET_META } from "../lib/api";

// Teer gaming day: rolls over at 1 AM IST
const IST_TODAY = () => new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const prettyDayShort = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const prettyDDMM = (iso) => {
    if (!iso) return "";
    const [y, m, d] = String(iso).slice(0, 10).split("-");
    return `${d}.${m}`;
};

/**
 * AllGamesQuickView — replaces the 3 static info cards.
 *
 * Renders TWO sections:
 *  1. Common Numbers for ALL games (grid of 7 market cards, tap to see details)
 *  2. Previous Results — one row per game (chronological last 7 days horizontally)
 */
export const AllGamesQuickView = () => {
    const [commonByMarket, setCommonByMarket] = useState({});
    const [prevByMarket, setPrevByMarket] = useState({});
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            // Auto common numbers for today (all markets) — deterministic, same for all visitors
            const cn = await api.get("/common-number/today");
            const cnMap = {};
            (cn.data || []).forEach((p) => { cnMap[p.market_key] = p; });
            setCommonByMarket(cnMap);

            // All results — group by market, latest 7 per market
            const rs = await api.get("/results", { params: { limit: 500 } });
            const byMarket = {};
            const today = IST_TODAY();
            (rs.data || []).forEach((r) => {
                if (r.date > today) return; // ignore future entries
                if (!byMarket[r.market_key]) byMarket[r.market_key] = [];
                byMarket[r.market_key].push(r);
            });
            // Sort each market's results by date desc and cap to 7
            Object.keys(byMarket).forEach((k) => {
                byMarket[k].sort((a, b) => (a.date < b.date ? 1 : -1));
                byMarket[k] = byMarket[k].slice(0, 7);
            });
            setPrevByMarket(byMarket);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <>
            {/* ---- Section 1: All Games Common Number ---- */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-testid="section-all-common-numbers">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-yellow-600 font-semibold">All Games</div>
                        <h2 className="font-display font-bold text-2xl md:text-3xl text-blue-950 tracking-tight mt-1">
                            Today's Common Number — Every Game
                        </h2>
                        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
                            Ek jagah pe sabhi khelon ke aaj ke common numbers. Kisi bhi card pe tap karke full list dekh sakte hain.
                        </p>
                    </div>
                    <Link
                        to="/common-number"
                        data-testid="all-common-cta"
                        className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                    >
                        View full chart <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {MARKET_ORDER.map((k) => {
                        const meta = MARKET_META[k];
                        const p = commonByMarket[k];
                        const dmy = p?.date
                            ? (() => { const [y,m,d]=p.date.split("-"); return `${d}/${m}/${y}`; })()
                            : (() => {
                                const iso = new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                                const [y,m,d] = iso.split("-");
                                return `${d}/${m}/${y}`;
                              })();
                        const shortLabel = meta.name.replace(/ Teer$/i, "").toUpperCase();
                        return (
                            <Link
                                key={k}
                                to="/common-number"
                                data-testid={`common-market-card-${k}`}
                                className="group rounded-md bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                                style={{ borderTop: `3px solid ${meta.accent}` }}
                            >
                                {loading ? (
                                    <div className="p-2"><div className="h-16 skeleton rounded" /></div>
                                ) : (
                                    // ---- Always show full structured layout (F/R and S/R side-by-side) ----
                                    <div>
                                        <div className="px-2 py-1 text-center font-num font-bold text-slate-900 text-xs" data-testid={`common-date-${k}`}>
                                            {dmy}
                                        </div>
                                        <div className="px-2 py-0.5 text-center bg-slate-100 border-y border-slate-200 font-display font-bold uppercase text-slate-900 text-[11px] tracking-wider">
                                            {shortLabel}
                                        </div>
                                        <div className="grid grid-cols-2 divide-x divide-slate-300">
                                            {[
                                                { label: "F/R", direct: p?.fr_direct, house: p?.fr_house, ending: p?.fr_ending },
                                                { label: "S/R", direct: p?.sr_direct, house: p?.sr_house, ending: p?.sr_ending },
                                            ].map((row, idx) => (
                                                <div key={idx}>
                                                    <div className="text-center py-0.5 text-[9px] font-semibold text-slate-500 border-b border-slate-200 bg-slate-50">
                                                        {row.label}
                                                    </div>
                                                    <div className="grid grid-cols-3 bg-emerald-300/80 border-b border-slate-200">
                                                        <div className="py-0.5 text-center text-slate-900 border-r border-slate-200 text-[9px]">Direct</div>
                                                        <div className="py-0.5 text-center text-slate-900 border-r border-slate-200 text-[9px]">House</div>
                                                        <div className="py-0.5 text-center text-slate-900 text-[9px]">Ending</div>
                                                    </div>
                                                    <div className="grid grid-cols-3">
                                                        <div className="py-1 text-center border-r border-slate-200 result-digit text-sm text-slate-900" data-testid={`common-direct-${row.label.toLowerCase().replace('/','')}-${k}`}>
                                                            {row.direct && row.direct.length ? row.direct.join(",") : "—"}
                                                        </div>
                                                        <div className="py-1 text-center border-r border-slate-200 result-digit text-sm text-slate-900" data-testid={`common-house-${row.label.toLowerCase().replace('/','')}-${k}`}>
                                                            {row.house || "—"}
                                                        </div>
                                                        <div className="py-1 text-center result-digit text-sm text-slate-900" data-testid={`common-ending-${row.label.toLowerCase().replace('/','')}-${k}`}>
                                                            {row.ending || "—"}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ---- Section 2: Previous Results — one row per game ---- */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14" data-testid="section-all-previous">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-yellow-600 font-semibold">All Games · Day &amp; Night</div>
                        <h2 className="font-display font-bold text-2xl md:text-3xl text-blue-950 tracking-tight mt-1">
                            Previous Results — Game-wise
                        </h2>
                        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
                            Har khel ka previous result alag-alag line mein — juwai ki line juwai ki, khanapara ki khanapara ki. Neeche full archive bhi hai.
                        </p>
                    </div>
                    <Link
                        to="/previous-results"
                        data-testid="all-previous-cta"
                        className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                    >
                        Full archive <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>

                <div className="mt-5 space-y-3">
                    {MARKET_ORDER.map((k) => {
                        const meta = MARKET_META[k];
                        const rows = prevByMarket[k] || [];
                        return (
                            <div
                                key={k}
                                className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden"
                                data-testid={`prev-row-${k}`}
                            >
                                <div
                                    className="px-3 py-2 flex items-center gap-2 text-white"
                                    style={{ background: meta.accent, color: meta.headerTextColor || "#FFFFFF" }}
                                >
                                    <span className="text-base" aria-hidden="true">{meta.emoji}</span>
                                    <span className="font-display font-bold text-sm sm:text-base tracking-tight">
                                        {meta.name} — Previous Results
                                    </span>
                                    <Link
                                        to="/previous-results"
                                        data-testid={`prev-row-cta-${k}`}
                                        className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold bg-white/20 px-2 py-1 rounded-full hover:bg-white/30 transition-colors"
                                    >
                                        View all <History className="h-3 w-3" />
                                    </Link>
                                </div>

                                {loading ? (
                                    <div className="p-4"><div className="h-14 skeleton rounded" /></div>
                                ) : rows.length === 0 ? (
                                    <div className="p-4 text-xs text-slate-400 flex items-center gap-2">
                                        <History className="h-4 w-4" /> Abhi tak koi previous result upload nahi hua.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                                                    <th className="px-3 py-2 text-left">Date</th>
                                                    {rows.map((r) => (
                                                        <th key={r.date} className="px-2 py-2 text-center font-num" data-testid={`prev-date-${k}-${r.date}`}>
                                                            {prettyDDMM(r.date)}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="border-b border-slate-100">
                                                    <td className="px-3 py-2 text-blue-900 font-semibold text-xs">F/R</td>
                                                    {rows.map((r) => (
                                                        <td key={r.date} className="px-2 py-2 text-center">
                                                            <span
                                                                className={`inline-block result-digit text-base ${r.fr === null || r.fr === undefined || r.fr === "" ? "text-slate-300" : "text-blue-900"}`}
                                                                data-testid={`prev-fr-${k}-${r.date}`}
                                                            >
                                                                {r.fr === null || r.fr === undefined || r.fr === "" ? "—" : String(r.fr).padStart(2, "0")}
                                                            </span>
                                                        </td>
                                                    ))}
                                                </tr>
                                                <tr>
                                                    <td className="px-3 py-2 text-blue-900 font-semibold text-xs">S/R</td>
                                                    {rows.map((r) => (
                                                        <td key={r.date} className="px-2 py-2 text-center">
                                                            <span
                                                                className={`inline-block result-digit text-base ${r.sr === null || r.sr === undefined || r.sr === "" ? "text-slate-300" : "text-blue-900"}`}
                                                                data-testid={`prev-sr-${k}-${r.date}`}
                                                            >
                                                                {r.sr === null || r.sr === undefined || r.sr === "" ? "—" : String(r.sr).padStart(2, "0")}
                                                            </span>
                                                        </td>
                                                    ))}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </>
    );
};
